import classNames from 'classnames';
import { memo, type ReactElement, type ReactNode } from 'react';

import { Copy } from '@air/next-icons';
import { Button } from '@air/primitive-button';

import { type TableItem } from '~/components/DevModal/types';

/**
 * Configuration for table header columns.
 */
export type TableHeaderColumn = {
  /** Display label for the column */
  label: string;
  /** Number of grid columns to span (default: 1) */
  span?: number;
  /** Text alignment within the column */
  align?: 'left' | 'right';
};

/**
 * Configuration constants for the resizable panel.
 */
const PANEL_CONFIG = {
  MIN_WIDTH: 200,
  MAX_WIDTH: 600,
  DEFAULT_GRID_COLS: 4,
} as const;

/**
 * Props for the DataTableWithDetails component.
 */
export type DataTableWithDetailsProps<T extends TableItem> = {
  /** Current width of the left panel in pixels */
  leftPanelWidth: number;
  /** Callback to update the left panel width */
  setLeftPanelWidth: (width: number) => void;
  /** Configuration for table header columns */
  headerColumns: TableHeaderColumn[];
  /** Array of data items to display in the table */
  data: T[];
  /** Currently selected item (null if none selected) */
  selectedItem: T | null;
  /** Callback to update the selected item */
  setSelectedItem: (item: T | null) => void;
  /** Function to render each table row */
  renderRow: (item: T, isSelected: boolean) => ReactNode;
  /** Function to render the details panel content */
  renderDetails: (item: T) => ReactNode;
  /** Message to display when the table is empty */
  emptyMessage: string;
  /** Title for the details panel */
  detailsTitle: string;
  /** Text for the copy button */
  copyButtonText: string;
  /** Callback when the copy button is clicked */
  onCopy: (item: T) => void;
};

/**
 * A reusable component that displays a data table with a resizable details panel.
 *
 * Features:
 * - Resizable left panel with configurable min/max widths
 * - Selectable rows with visual feedback
 * - Customizable column headers with spanning and alignment
 * - Details panel that shows information for the selected item
 * - Copy functionality for selected items
 * - Empty state handling
 *
 * @example
 * ```tsx
 * <DataTableWithDetails
 *   data={items}
 *   selectedItem={selectedItem}
 *   setSelectedItem={setSelectedItem}
 *   renderRow={(item, isSelected) => <div>{item.name}</div>}
 *   renderDetails={(item) => <div>{item.description}</div>}
 *   headerColumns={[{ label: 'Name', span: 2 }, { label: 'Status' }]}
 *   emptyMessage="No items found"
 *   detailsTitle="Item Details"
 *   copyButtonText="Copy"
 *   onCopy={(item) => navigator.clipboard.writeText(item.name)}
 *   leftPanelWidth={400}
 *   setLeftPanelWidth={setWidth}
 * />
 * ```
 */
function DataTableWithDetailsComponent<T extends TableItem>({
  leftPanelWidth,
  setLeftPanelWidth,
  headerColumns,
  data,
  selectedItem,
  setSelectedItem,
  renderRow,
  renderDetails,
  emptyMessage,
  detailsTitle,
  copyButtonText,
  onCopy,
}: DataTableWithDetailsProps<T>) {
  /**
   * Handles the resize drag interaction for the panel separator.
   */
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = leftPanelWidth;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = Math.max(PANEL_CONFIG.MIN_WIDTH, Math.min(PANEL_CONFIG.MAX_WIDTH, startWidth + deltaX));
      setLeftPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleRowClick = (item: T) => {
    setSelectedItem(selectedItem?.id === item.id ? null : item);
  };

  return (
    <div className="flex min-h-0 flex-1">
      {/* Left Panel - Data Table */}
      <div className="flex min-h-0 flex-col border-r" style={{ width: `${leftPanelWidth}px` }}>
        {/* Table Header */}
        <div
          className="grid shrink-0 gap-2 border-b bg-grey-3 px-3 py-2 font-medium text-12 text-grey-12"
          style={{ gridTemplateColumns: `repeat(${PANEL_CONFIG.DEFAULT_GRID_COLS}, 1fr)` }}
        >
          {headerColumns.map((column, index) => (
            <span
              key={`header-${index}`}
              className={column.align === 'right' ? 'text-right' : 'text-left'}
              style={{ gridColumn: column.span ? `span ${column.span}` : 'span 1' }}
            >
              {column.label}
            </span>
          ))}
        </div>

        {/* Table Body */}
        <div className="flex-1 overflow-y-auto">
          {data.length === 0 ? (
            <div className="px-3 py-8 text-center text-12 text-grey-12">{emptyMessage}</div>
          ) : (
            data.map((item) => {
              const isSelected = selectedItem?.id === item.id;
              return (
                <div
                  key={`row-${item.id}`}
                  className={classNames(
                    'cursor-pointer border-b px-3 py-2 text-12 transition-colors duration-150 hover:bg-grey-5',
                    isSelected ? 'border-blue-2 bg-blue-5' : '',
                  )}
                  onClick={() => handleRowClick(item)}
                >
                  {renderRow(item, isSelected)}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Resizable Separator */}
      <div
        className="w-1 shrink-0 cursor-col-resize bg-grey-3 transition-colors hover:bg-grey-4"
        onMouseDown={handleResizeMouseDown}
        title="Drag to resize panels"
      />

      {/* Right Panel - Details */}
      <div className="flex min-h-0 flex-1 flex-col">
        {selectedItem ? (
          <div className="flex-1 space-y-4 overflow-y-auto p-3">
            <div className="space-y-2">
              {/* Details Header */}
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-14 text-grey-12">{detailsTitle}</h4>
                <Button
                  type="button"
                  size="small"
                  appearance="outline"
                  color="blue"
                  onClick={() => onCopy(selectedItem)}
                  prefix={<Copy className="size-3" />}
                >
                  {copyButtonText}
                </Button>
              </div>

              {/* Details Content */}
              {renderDetails(selectedItem)}
            </div>
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center text-12 text-grey-12">
            Select an item to view details
          </div>
        )}
      </div>
    </div>
  );
}

type DataTableWithDetailsGeneric = <T extends TableItem>(props: DataTableWithDetailsProps<T>) => ReactElement;

export const DataTableWithDetails = memo(DataTableWithDetailsComponent) as DataTableWithDetailsGeneric & {
  displayName?: string;
};

DataTableWithDetails.displayName = 'DataTableWithDetails';
